/**
 * Reads and writes of a case's hypotheses (ADR-0095). Every write refreshes the list and the
 * hypothesis it touched; a refusal of the server comes back as the message the form shows.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import { analyticHypothesesApi } from '@/services/api/analytic-hypotheses'
import { ApiError } from '@/services/api/client'
import type {
  AnalyticHypothesisCreate,
  HypothesisAssessmentCreate,
  HypothesisRefusal,
} from '@/types/analytic-hypothesis'

export const analyticHypothesisKeys = {
  all: (caseId: string) => ['case', caseId, 'analytic-hypotheses'] as const,
  one: (caseId: string, hypothesisId: string) =>
    [...analyticHypothesisKeys.all(caseId), hypothesisId] as const,
  /** Under the hypothesis's own key: refreshing the hypothesis refreshes what it rests on. */
  researchRuns: (caseId: string, hypothesisId: string) =>
    [...analyticHypothesisKeys.one(caseId, hypothesisId), 'research-runs'] as const,
  investigations: (caseId: string, hypothesisId: string) =>
    [...analyticHypothesisKeys.one(caseId, hypothesisId), 'investigations'] as const,
}

/**
 * A read's key as of a date (*Spis k dátumu*, PRD-0075 R-D4): under the key of the read as of
 * now, so whatever refreshes one refreshes the other.
 */
export function asOfKey<K extends readonly unknown[]>(key: K, asOf: string | undefined) {
  return asOf ? ([...key, { asOf }] as const) : key
}

/** A refusal (not a member, unknown id) answers the same again; an outage may not. */
export function retryUnlessRefused(failureCount: number, error: unknown): boolean {
  const refused = error instanceof ApiError && error.status >= 400 && error.status < 500
  return !refused && failureCount < 2
}

/** The read was answered 404: no such hypothesis (the id may be a research one). */
export function isNotFound(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404
}

function refusalOf(error: unknown): HypothesisRefusal | null {
  if (!(error instanceof ApiError)) return null
  const detail = (error.data as { detail?: unknown } | undefined)?.detail
  return detail && typeof detail === 'object' && 'code' in detail
    ? (detail as HypothesisRefusal)
    : null
}

/** What the form says when a write fails. */
export function refusalMessage(error: unknown, t: TFunction, fallbackKey: string): string {
  const refusal = refusalOf(error)
  if (refusal?.code === 'unknown_focus_entities') return t('analyticHypotheses.create.unknownFocus')
  if (refusal?.code === 'unknown_evidence') return t('analyticHypotheses.form.unknownEvidence')
  if (refusal?.code === 'hypothesis_closed') return t('analyticHypotheses.form.closed')
  if (error instanceof ApiError && error.status === 403) return t('analyticHypotheses.readOnly')
  return t(fallbackKey)
}

/** The case's hypotheses, as they stood at `asOf` when given. */
export function useAnalyticHypotheses(caseId: string, asOf?: string) {
  return useQuery({
    queryKey: asOfKey(analyticHypothesisKeys.all(caseId), asOf),
    queryFn: ({ signal }) => analyticHypothesesApi.list(caseId, { asOf, signal }),
    enabled: Boolean(caseId),
    retry: retryUnlessRefused,
  })
}

/** One hypothesis, as it stood at `asOf` when given. */
export function useAnalyticHypothesis(caseId: string, hypothesisId: string, asOf?: string) {
  return useQuery({
    queryKey: asOfKey(analyticHypothesisKeys.one(caseId, hypothesisId), asOf),
    queryFn: ({ signal }) => analyticHypothesesApi.get(caseId, hypothesisId, { asOf, signal }),
    enabled: Boolean(caseId && hypothesisId),
    retry: retryUnlessRefused,
  })
}

/** A write's result: what the server answered, or the message the form shows. */
export type WriteOutcome<T> =
  | { ok: true; value: T; message?: undefined }
  | { ok: false; value?: undefined; message: string }

/** Runs a write and answers its outcome; a failure reads as its refusal's message. */
export async function settle<T>(
  run: () => Promise<T>,
  t: TFunction,
  fallbackKey: string
): Promise<WriteOutcome<T>> {
  try {
    return { ok: true, value: await run() }
  } catch (error) {
    return { ok: false, message: refusalMessage(error, t, fallbackKey) }
  }
}

/** The case's hypothesis writes; each refreshes the list and the hypothesis it touched. */
export function useHypothesisWrites(caseId: string) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  // The list's key prefixes every hypothesis's own key: one invalidation refreshes both.
  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: analyticHypothesisKeys.all(caseId) })

  const create = useMutation({
    mutationFn: (body: AnalyticHypothesisCreate) => analyticHypothesesApi.create(caseId, body),
    onSuccess: refresh,
  })
  const assess = useMutation({
    mutationFn: (input: { hypothesisId: string; body: HypothesisAssessmentCreate }) =>
      analyticHypothesesApi.addAssessment(caseId, input.hypothesisId, input.body),
    onSuccess: refresh,
  })
  const status = useMutation({
    mutationFn: (input: { hypothesisId: string; close: boolean }) =>
      input.close
        ? analyticHypothesesApi.close(caseId, input.hypothesisId)
        : analyticHypothesesApi.reopen(caseId, input.hypothesisId),
    onSuccess: refresh,
  })

  return {
    create: (body: AnalyticHypothesisCreate) =>
      settle(() => create.mutateAsync(body), t, 'analyticHypotheses.create.failed'),
    addAssessment: (hypothesisId: string, body: HypothesisAssessmentCreate) =>
      settle(() => assess.mutateAsync({ hypothesisId, body }), t, 'analyticHypotheses.form.failed'),
    setClosed: (hypothesisId: string, close: boolean) =>
      settle(
        () => status.mutateAsync({ hypothesisId, close }),
        t,
        'analyticHypotheses.detail.statusFailed'
      ),
    statusPending: status.isPending,
  }
}

/** A form's answer: null once done, else the message it shows. */
export function messageOf<T>(outcome: WriteOutcome<T>): string | null {
  return outcome.message ?? null
}
